/**
 * ClauseReader.tsx — Tier 2 focused reading overlay
 *
 * A right-anchored overlay that shows the full clause body + page images.
 * Triggered from the Tier 1 expanded node card via "Read full clause ↗" or
 * clicking a page thumbnail.
 *
 * Layout:
 * - Wide (≥720px panel): two-column — clause text left, PageViewer right
 * - Narrow (<720px): segmented Text | Page toggle
 *
 * Esc closes and returns focus to the expanded node.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import PageViewer from './PageViewer';
import { getPageImageUrl } from '../api';
import type { PageInfo } from '../types';
import type { ClauseFull } from '../hooks/useClauseDetail';

interface Props {
  clauseFull: ClauseFull;
  pages: PageInfo[];
  initialPage?: number;
  onClose: () => void;
}

export default function ClauseReader({ clauseFull, pages, initialPage, onClose }: Props) {
  const [activePage, setActivePage] = useState<number>(initialPage ?? pages[0]?.page_start ?? 0);
  const [activeTab, setActiveTab] = useState<'text' | 'page'>('text');
  const containerRef = useRef<HTMLDivElement>(null);

  const totalPages = pages.length;
  const activePageInfo = pages.find(p => p.page_start === activePage) ?? pages[0] ?? null;

  // Esc handler
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
      if ((e.key === '[') && pages.length > 1) {
        const idx = pages.findIndex(p => p.page_start === activePage);
        if (idx > 0) setActivePage(pages[idx - 1].page_start);
      }
      if ((e.key === ']') && pages.length > 1) {
        const idx = pages.findIndex(p => p.page_start === activePage);
        if (idx < pages.length - 1) setActivePage(pages[idx + 1].page_start);
      }
    };
    window.addEventListener('keydown', handleKey, true);
    return () => window.removeEventListener('keydown', handleKey, true);
  }, [onClose, activePage, pages]);

  // Focus trap
  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  const pageLabel = (p: PageInfo) => `p.${p.page_start}`;

  const header = (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px',
      borderBottom: '1px solid #2e2e33', flexShrink: 0,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: '#6366f1', fontFamily: 'monospace', fontWeight: 700 }}>
          {clauseFull.standard}
        </div>
        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {clauseFull.clause_number ? `Clause ${clauseFull.clause_number}` : clauseFull.title}
          {totalPages > 0 && activePageInfo && (
            <span style={{ color: '#52525b', marginLeft: 6 }}>
              · Page {activePageInfo.page_start}
              {totalPages > 1 ? ` of ${pages[pages.length - 1].page_start}` : ''}
            </span>
          )}
        </div>
      </div>
      {/* Page nav */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {pages.map((p, i) => (
            <button
              key={p.page_start}
              onClick={() => setActivePage(p.page_start)}
              style={{
                padding: '2px 6px', borderRadius: 4, fontSize: 10, cursor: 'pointer',
                border: p.page_start === activePage ? '1px solid #6366f1' : '1px solid #2e2e33',
                background: p.page_start === activePage ? 'rgba(99,102,241,0.15)' : 'transparent',
                color: p.page_start === activePage ? '#a5b4fc' : '#64748b',
              }}
            >
              {pageLabel(p)}{i > 0 ? ' cont.' : ''}
            </button>
          ))}
        </div>
      )}
      <button
        onClick={onClose}
        aria-label="Close reader"
        style={{
          background: 'none', border: 'none', color: '#64748b', cursor: 'pointer',
          fontSize: 16, padding: '2px 6px', flexShrink: 0,
        }}
      >✕</button>
    </div>
  );

  // F47: corpus bodies carry machine markers (<!-- xref: ... -->, <!-- FIGREF ... -->)
  // and markdown syntax. The old <pre> dumped both raw at the user — strip the
  // comments and render the markdown like every other surface does.
  const displayBody = (clauseFull.body || '').replace(/<!--[\s\S]*?-->/g, '').replace(/\n{3,}/g, '\n\n').trim();

  const textPane = (
    <div style={{
      flex: '1 1 45%', overflowY: 'auto', padding: '16px', minWidth: 0,
    }}>
      <h3 style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', margin: '0 0 12px 0', lineHeight: 1.4 }}>
        {clauseFull.title}
      </h3>
      <div className="clause-reader-body" style={{ fontSize: 13, lineHeight: 1.7, color: '#cbd5e1', wordBreak: 'break-word' }}>
        {displayBody
          ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayBody}</ReactMarkdown>
          : '(no text on file)'}
      </div>
    </div>
  );

  // F48: the image column was a fixed 240px sliver inside a 560px panel —
  // unusable for reading an A4 standards page. Proportional split instead.
  const imagePane = activePageInfo?.has_image ? (
    <div style={{ flex: '1 1 55%', minWidth: 320, borderLeft: '1px solid #2e2e33', overflowY: 'auto' }}>
      <PageViewer
        src={activePageInfo.image_url ?? getPageImageUrl(activePageInfo.pdf_id, activePageInfo.page_start)}
        alt={`${clauseFull.standard} page ${activePageInfo.page_start}`}
        pageNumber={activePageInfo.page_start}
      />
    </div>
  ) : (
    <div style={{
      flex: '0 0 240px', borderLeft: '1px solid #2e2e33',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, color: '#52525b', padding: 16, textAlign: 'center',
    }}>
      {totalPages === 0 ? 'No page images mapped for this clause' : 'Page image not available'}
    </div>
  );

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      role="dialog"
      aria-label={`Clause reader: ${clauseFull.standard} ${clauseFull.clause_number ?? ''}`}
      style={{
        position: 'absolute', right: 0, top: 0, bottom: 0, width: 'min(960px, 94vw)',
        zIndex: 2000, background: '#0f0f11',
        borderLeft: '1px solid #2e2e33',
        display: 'flex', flexDirection: 'column',
        outline: 'none', overflowX: 'hidden',
      }}
    >
      {header}

      {/* Wide layout: side by side */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {textPane}
        {imagePane}
      </div>

      {/* Narrow mode tab toggle — hidden at 560px but shown via media query if needed */}
      <div style={{
        display: 'none', // Only shown in narrow via className — kept simple here
        padding: '8px 16px', borderTop: '1px solid #2e2e33', gap: 8,
      }}>
        <button
          onClick={() => setActiveTab('text')}
          style={{ padding: '4px 10px', fontSize: 12, borderRadius: 4, border: 'none', cursor: 'pointer',
            background: activeTab === 'text' ? '#6366f1' : 'transparent',
            color: activeTab === 'text' ? '#fff' : '#64748b' }}
        >Text</button>
        <button
          onClick={() => setActiveTab('page')}
          style={{ padding: '4px 10px', fontSize: 12, borderRadius: 4, border: 'none', cursor: 'pointer',
            background: activeTab === 'page' ? '#6366f1' : 'transparent',
            color: activeTab === 'page' ? '#fff' : '#64748b' }}
        >Page</button>
      </div>
    </div>
  );
}
